// src/components/kuestion/shared/Tour.jsx
//
// The first-login tour and the per-section intros (web mockup, 17 Sep 2026).
// One control lit, everything else dimmed. The scrim is a single huge
// box-shadow spread from the highlight box, so the cut-out is always exactly
// the measured element and there are no four-panel seams to line up.
//
// What has been seen is remembered per user on this device, under the Kuestion
// storage namespace. It is a convenience, not account state: a new browser
// shows the tour again, and the question mark beside the name replays it.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mark } from './Bits';
import { buildTour, SECTIONS } from './tourSteps';
import { kq, storageKey } from '../../../config/routes';

const TourCtx = createContext(null);
export const useTour = () => useContext(TourCtx);

const readSeen = (key) => { try { return JSON.parse(localStorage.getItem(key) || 'null') || {}; } catch { return {}; } };
const writeSeen = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode */ } };

const CARD_W = 400;
const EST_H = 300; // card height estimate, for choosing above or below

// Narrow targets are rail icons, so the card sits beside them; wide ones get
// the card below, or above when the target is already near the bottom.
function placeCard(rect) {
  const vw = window.innerWidth; const vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - 32);
  if (!rect) return { width: w, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  const clampL = (x) => Math.max(16, Math.min(x, vw - w - 16));
  if (rect.w < 120 && rect.l + rect.w + 20 + w < vw) {
    return { width: w, left: clampL(rect.l + rect.w + 20), top: Math.max(16, Math.min(rect.t - 30, vh - EST_H - 40)) };
  }
  const below = rect.t + rect.h + 18;
  const above = rect.t - 18 - EST_H;
  const top = below + EST_H < vh ? below : Math.max(16, Math.min(above, vh - EST_H - 16));
  return { width: w, left: clampL(rect.l), top };
}

export function TourProvider({ mode, userId, firstName, orgName, enabled = true, children }) {
  const navigate = useNavigate();
  const key = storageKey('kuestion', `tour.${userId || 'anon'}`);
  const [seen, setSeen] = useState(() => readSeen(key));
  const [run, setRun] = useState(null);      // { steps, intro: sectionKey | null }
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const nextBtn = useRef(null);

  const sections = SECTIONS[mode === 'solo' ? 'solo' : 'campus'];
  const mark = useCallback((patch) => setSeen((s) => { const n = { ...s, ...patch }; writeSeen(key, n); return n; }), [key]);

  const startTour = useCallback(() => {
    setRun({ steps: buildTour(mode, { firstName, orgName }), intro: null });
    setStep(0); setRect(null);
  }, [mode, firstName, orgName]);

  // First sign-in on this device: the tour offers itself once.
  useEffect(() => {
    if (!enabled || seen.tour) return;
    startTour();
    mark({ tour: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const close = useCallback(() => { setRun(null); setRect(null); }, []);

  // First visit to a section from the rail gets its intro — but never on top
  // of the main tour, which is already explaining the place.
  const visit = useCallback((sectionKey) => {
    if (!enabled || run || !sections[sectionKey] || seen[`s.${sectionKey}`]) return;
    mark({ [`s.${sectionKey}`]: true });
    setRun({ steps: sections[sectionKey].steps, intro: sectionKey });
    setStep(0); setRect(null);
  }, [enabled, run, sections, seen, mark]);

  const introsLeft = useMemo(() => Object.keys(sections).filter((k) => !seen[`s.${k}`]), [sections, seen]);

  // The bell: open the next unseen section; with none left, replay the tour.
  const nextIntro = useCallback(() => {
    if (!introsLeft.length) { startTour(); return; }
    const k = introsLeft[0];
    navigate(sections[k].to === '' ? kq() : kq(sections[k].to));
    visit(k);
  }, [introsLeft, sections, navigate, visit, startTour]);

  const current = run ? run.steps[step] : null;

  // Each step names the screen it belongs to.
  useEffect(() => {
    if (current && current.to != null) navigate(current.to === '' ? kq() : kq(current.to));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, step]);

  // Measure the target. Screens load their data after mount, so the anchor can
  // appear late or move: keep looking for a moment, and follow resize/scroll.
  useEffect(() => {
    if (!current) return undefined;
    if (!current.target) { setRect(null); return undefined; }
    let tries = 0; let timer = null; let alive = true; let ro = null; let watched = null;
    const measure = () => {
      if (!alive) return;
      const el = document.querySelector(`[data-tour="${current.target}"]`);
      if (el) {
        // Layout keeps settling after data arrives (a scrollbar appears, a grid
        // reflows): follow the element's own size, not just the window's.
        if (watched !== el && typeof ResizeObserver !== 'undefined') {
          if (ro) ro.disconnect();
          ro = new ResizeObserver(() => { tries = 10; measure(); });
          ro.observe(el); ro.observe(document.documentElement); watched = el;
        }
        const r = el.getBoundingClientRect();
        if (r.width > 0 || r.height > 0) {
          if (r.top < 0 || r.bottom > window.innerHeight) { try { el.scrollIntoView({ block: 'center' }); } catch { /* jsdom */ } }
          const b = el.getBoundingClientRect();
          setRect((cur) => {
            const n = { t: Math.round(b.top), l: Math.round(b.left), w: Math.round(b.width), h: Math.round(b.height) };
            return cur && cur.t === n.t && cur.l === n.l && cur.w === n.w && cur.h === n.h ? cur : n;
          });
        }
      }
      tries += 1;
      if (tries < 12) timer = setTimeout(measure, tries < 4 ? 80 : 250);
    };
    measure();
    const again = () => { tries = 10; measure(); };
    window.addEventListener('resize', again);
    window.addEventListener('scroll', again, true);
    return () => { alive = false; clearTimeout(timer); if (ro) ro.disconnect(); window.removeEventListener('resize', again); window.removeEventListener('scroll', again, true); };
  }, [current]);

  useEffect(() => {
    if (!run) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    if (nextBtn.current) nextBtn.current.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [run, step, close]);

  const value = useMemo(() => ({
    startTour, visit, nextIntro,
    // true while the tour or a section intro is on screen: the noticeboard
    // holds its auto-open until this clears, so the two never stack
    active: Boolean(run),
    introsLeft: introsLeft.length,
    introsTitle: introsLeft.length === 0 ? 'Every section seen. Replay the tour'
      : introsLeft.length === 1 ? `One section left to see: ${sections[introsLeft[0]].label}`
        : `${introsLeft.length} sections left to see — ${introsLeft.map((k) => sections[k].label).join(', ')}`,
  }), [startTour, visit, nextIntro, introsLeft, sections, run]);

  const total = run ? run.steps.length : 0;
  const last = step === total - 1;
  const isIntro = Boolean(run && run.intro);
  const next = () => (last ? close() : (setRect(null), setStep((i) => i + 1)));

  return (
    <TourCtx.Provider value={value}>
      {children}
      {current && (
        <div className="kqc kqc-tour-host"><div className="kqc-tour" data-testid="tour">
          {rect
            ? <div className="kqc-tour__spot" aria-hidden="true" style={{ top: rect.t, left: rect.l, width: rect.w, height: rect.h }} />
            : <div className="kqc-tour__scrim" aria-hidden="true" />}
          <div className="kqc-tour__card" role="dialog" aria-modal="true" aria-label={isIntro ? `About ${sections[run.intro].label}` : 'Getting started'} style={placeCard(rect)}>
            <div className="kqc-tour__head">
              <span className="kqc-tour__mark"><Mark /></span>
              <span>{current.kicker}</span>
              <span className="kqc-spacer" />
              {/* No counter on a one-card intro: "1 of 1" implies a sequence that does not exist. */}
              {total > 1 && <span className="kqc-tour__count">{step + 1} of {total}</span>}
            </div>
            <div className="kqc-tour__body">
              <h2 className={current.big ? 'is-big' : undefined}>{current.title}</h2>
              <p>{current.body}</p>
              {current.aside && <div className="kqc-tour__aside"><i aria-hidden="true" /><span>{current.aside}</span></div>}
              {total > 1 && (
                <div className="kqc-tour__dots" aria-hidden="true">
                  {run.steps.map((_, i) => <span key={i} className={i === step ? 'is-on' : i < step ? 'is-done' : undefined} />)}
                </div>
              )}
            </div>
            <div className="kqc-tour__foot">
              <button type="button" ref={nextBtn} className="btn btn-primary" onClick={next}>
                {last ? (isIntro ? 'Got it' : 'Start using Kuestion') : (step === 0 && !isIntro ? 'Show me around' : 'Next')}
              </button>
              {step > 0 && <button type="button" className="btn btn-secondary" onClick={() => { setRect(null); setStep((i) => Math.max(0, i - 1)); }}>Back</button>}
              <span className="kqc-spacer" />
              {total > 1 && <button type="button" className="btn btn-ghost" onClick={close}>{last ? 'Close' : 'Skip'}</button>}
            </div>
          </div>
        </div></div>
      )}
    </TourCtx.Provider>
  );
}

// The two controls the shells place: a bell on the rail, a question mark by the name.
export function TourBell() {
  const t = useTour();
  if (!t) return null;
  return (
    <button type="button" className={`kqc-rail__btn kqc-rail__bell${t.introsLeft ? '' : ' kqc-rail__btn--dim'}`} title={t.introsTitle} aria-label={t.introsTitle} onClick={t.nextIntro}>
      <span className="kqc-rail__icon">
        <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8.5a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5" /><path d="M10.3 19a2 2 0 0 0 3.4 0" />
        </svg>
        {t.introsLeft > 0 && <b className="kqc-rail__badge">{t.introsLeft}</b>}
      </span>
      <span className="kqc-rail__label">Guide</span>
    </button>
  );
}

export function TourReplay() {
  const t = useTour();
  if (!t) return null;
  return (
    <button type="button" className="kqc-tourbtn" title="Show me around again" aria-label="Show me around again" onClick={t.startTour}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9.2" /><path d="M9.4 9.1a2.7 2.7 0 1 1 3.4 2.6c-.5.2-.8.6-.8 1.1v.5" /><line x1="12" y1="16.4" x2="12" y2="16.4" />
      </svg>
    </button>
  );
}
